import Link from "next/link"

import { Button } from "@/components/ui/button"
import {
  Carousel,
  CarouselContent,
  CarouselItem
} from "@/components/ui/carousel"
import type { CasinoProviderDTO } from "@/domain/casino/list-lobby-facets"
import { ProvidersIcon } from "@/src/shared/assets/icons"

import { casinoSeeAllHref } from "../data/lobby"
import {
  categoryGamesPreviewTexts as seeAll,
  lobbyTexts
} from "../localization/texts"
import { ProviderLogoImage } from "./ProviderLogoImage"

export type ProviderRailProps = {
  providers: CasinoProviderDTO[]
}

const PROVIDER_SKELETON_KEYS = [
  "provider-a",
  "provider-b",
  "provider-c",
  "provider-d",
  "provider-e",
  "provider-f"
] as const

export function ProviderRailSkeleton() {
  return (
    <section aria-hidden className="min-w-0 px-2">
      <div className="flex min-w-0 flex-col gap-3.5">
        <div className="flex items-center justify-between gap-2">
          <div className="h-5 w-32 rounded bg-default-100" />
          <div className="h-11 w-24 rounded bg-default-100" />
        </div>
        <div className="flex gap-2 overflow-hidden md:gap-2.5">
          {PROVIDER_SKELETON_KEYS.map((key) => (
            <div
              key={key}
              className="h-[78px] w-[160px] shrink-0 rounded-lg bg-default-100 md:h-[94px] md:w-[206px]"
            />
          ))}
        </div>
      </div>
    </section>
  )
}

export function ProviderRail({ providers }: ProviderRailProps) {
  const title = lobbyTexts.rails.providers

  if (providers.length === 0) {
    return null
  }

  return (
    <section aria-label={title} className="min-w-0 px-2">
      <div className="flex min-w-0 flex-col gap-3.5">
        <header className="flex items-center justify-between gap-2">
          <h2 className="flex min-w-0 items-center gap-2 font-gochi text-[20px] font-normal leading-none uppercase">
            <ProvidersIcon
              viewBox="0 0 20 20"
              className="block size-5 shrink-0 self-center overflow-visible"
              aria-hidden
            />
            <span className="truncate leading-none" style={{ transform: "translateY(3px)" }}>
              {title}
            </span>
          </h2>
          <Button asChild variant="muted">
            <Link
              href={casinoSeeAllHref}
              prefetch
              aria-label={`${seeAll.seeAllAria}: ${title}`}
            >
              {seeAll.button}
            </Link>
          </Button>
        </header>
        <Carousel className="w-full" opts={{ align: "start", dragFree: true }}>
          <CarouselContent className="gap-2 md:gap-2.5">
            {providers.map((provider) => (
              <CarouselItem
                key={provider.productCode}
                className="basis-[160px] md:basis-[206px]"
              >
                <div className="flex h-[78px] items-center justify-center rounded-lg bg-black-800 px-5 md:h-[94px]">
                  <ProviderLogoImage src={provider.logoUrl} alt={provider.name} />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      </div>
    </section>
  )
}
